import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EncerrarSessao, EncerrarTodasSessoes, ListarHistoricoLogins, ListarSessoesAtivas, UsuarioLoginDto } from './sessoes.service';
import { BiLogOut, BiTime, BiUser, BiXCircle } from 'react-icons/bi';

export default function SessoesPage() {
  const { setActions } = useBreadcrumbActions();
  const [sessoesAtivas, setSessoesAtivas] = useState<UsuarioLoginDto[]>([]);
  const [historico, setHistorico] = useState<UsuarioLoginDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'ativas' | 'historico'>('ativas');
  const [filtroUsuario] = useState<number | undefined>(undefined);
  const [encerrando, setEncerrando] = useState<number | null>(null);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      if (activeTab === 'ativas') {
        const dados = await ListarSessoesAtivas(filtroUsuario);
        setSessoesAtivas(dados);
      } else {
        const dados = await ListarHistoricoLogins(filtroUsuario, 100);
        setHistorico(dados);
      }
    } catch (error: any) {
      console.error('Erro ao carregar sessões:', error);
      toast.error(error?.message || 'Erro ao carregar sessões');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filtroUsuario]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    setActions({
      primary: { label: 'Atualizar', onClick: () => carregarDados() },
    });
    return () => setActions({});
  }, [setActions, carregarDados]);

  const handleEncerrarSessao = async (sessaoId: number) => {
    if (!window.confirm('Tem certeza que deseja encerrar esta sessão?')) {
      return;
    }

    try {
      setEncerrando(sessaoId);
      await EncerrarSessao(sessaoId);
      toast.success('Sessão encerrada com sucesso!');
      carregarDados();
    } catch (error: any) {
      console.error('Erro ao encerrar sessão:', error);
      toast.error(error?.message || 'Erro ao encerrar sessão');
    } finally {
      setEncerrando(null);
    }
  };

  const handleEncerrarTodasSessoes = async (usuarioId: number) => {
    if (!window.confirm('Tem certeza que deseja encerrar todas as sessões deste usuário?')) {
      return;
    }

    try {
      setEncerrando(usuarioId);
      await EncerrarTodasSessoes(usuarioId);
      toast.success('Todas as sessões do usuário foram encerradas!');
      carregarDados();
    } catch (error: any) {
      console.error('Erro ao encerrar sessões:', error);
      toast.error(error?.message || 'Erro ao encerrar sessões');
    } finally {
      setEncerrando(null);
    }
  };

  const formatarData = (data?: string | null) => {
    if (!data) return '-';
    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatarUserAgent = (userAgent?: string) => {
    if (!userAgent) return '-';
    // Extrai informações básicas do user agent
    if (userAgent.length > 80) {
      return userAgent.substring(0, 80) + '...';
    }
    return userAgent;
  };

  const renderSessoesAtivas = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      );
    }

    if (sessoesAtivas.length === 0) {
      return (
        <div className="alert alert-info" role="alert">
          Nenhuma sessão ativa no momento.
        </div>
      );
    }

    return (
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Usuário</th>
              <th>IP de Origem</th>
              <th>User-Agent</th>
              <th>Login em</th>
              <th>Último Acesso</th>
              <th className="text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {sessoesAtivas.map((sessao) => (
              <tr key={sessao.id}>
                <td>
                  <div>
                    <strong>{sessao.usuario.username}</strong>
                    {sessao.usuario.pessoa?.nome && (
                      <div className="text-muted small">{sessao.usuario.pessoa.nome}</div>
                    )}
                  </div>
                </td>
                <td>{sessao.ipOrigem || '-'}</td>
                <td>
                  <span className="text-muted small" title={sessao.userAgent}>
                    {formatarUserAgent(sessao.userAgent)}
                  </span>
                </td>
                <td>{formatarData(sessao.logadoEm)}</td>
                <td>{formatarData(sessao.ultimoAcesso)}</td>
                <td className="text-center">
                  <div className="btn-group" role="group">
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleEncerrarSessao(sessao.id)}
                      disabled={encerrando === sessao.id}
                      title="Encerrar esta sessão"
                    >
                      {encerrando === sessao.id ? (
                        <span className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <BiXCircle size={18} />
                      )}
                    </button>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => handleEncerrarTodasSessoes(sessao.usuario.id)}
                      disabled={encerrando === sessao.usuario.id}
                      title="Encerrar todas as sessões deste usuário"
                    >
                      <BiLogOut size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderHistorico = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      );
    }

    if (historico.length === 0) {
      return (
        <div className="alert alert-info" role="alert">
          Nenhum histórico de login encontrado.
        </div>
      );
    }

    return (
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Usuário</th>
              <th>IP de Origem</th>
              <th>User-Agent</th>
              <th>Login em</th>
              <th>Último Acesso</th>
              <th>Logout em</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {historico.map((sessao) => (
              <tr key={sessao.id}>
                <td>
                  <div>
                    <strong>{sessao.usuario.username}</strong>
                    {sessao.usuario.pessoa?.nome && (
                      <div className="text-muted small">{sessao.usuario.pessoa.nome}</div>
                    )}
                  </div>
                </td>
                <td>{sessao.ipOrigem || '-'}</td>
                <td>
                  <span className="text-muted small" title={sessao.userAgent}>
                    {formatarUserAgent(sessao.userAgent)}
                  </span>
                </td>
                <td>{formatarData(sessao.logadoEm)}</td>
                <td>{formatarData(sessao.ultimoAcesso)}</td>
                <td>{formatarData(sessao.deslogadoEm)}</td>
                <td>
                  <span className={`badge ${sessao.ativo ? 'bg-success' : 'bg-secondary'}`}>
                    {sessao.ativo ? 'Ativa' : 'Encerrada'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <h3 className="pt-4 mb-3">Controle de Sessões de Login</h3>
          <span className="blockquote-footer pb-4 text-end">
            Sessões ativas e histórico de logins.
          </span>
          <hr />
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'ativas' ? 'active' : ''}`}
            onClick={() => setActiveTab('ativas')}
            type="button"
          >
            <BiUser size={18} className="me-2" />
            Sessões Ativas ({sessoesAtivas.length})
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'historico' ? 'active' : ''}`}
            onClick={() => setActiveTab('historico')}
            type="button"
          >
            <BiTime size={18} className="me-2" />
            Histórico
          </button>
        </li>
      </ul>

      {/* Conteúdo das tabs */}
      <div className="tab-content">
        {activeTab === 'ativas' ? renderSessoesAtivas() : renderHistorico()}
      </div>
    </div>
  );
}

